import { isValidObject } from '@/helpers/common/validationHelper'
import { flattenStateItemObject } from '../common/helper'
import type { StdState, Device, UserCal, UploadStatus, StateItem } from '@/types/devices'

export interface DeviceDetails {
  timestamp?: string
  model?: string
  serial?: string
  name?: string | null
  locationName?: string | null
  indoor?: boolean | null
  isPublic?: boolean | null
  spsEoL?: boolean | null
  zeroState?: Record<string, string> | null
  zeroAppliedAt?: Record<string, string> | null
  config_timestamp?: string | null
  firmware?: string | null
  mqttHost?: string | null
  rssi?: number | null
  connectionType?: string | null
  calibrationDate?: string | null
  flowCalibrationFactor?: number | null
  userCal?: UserCal | null
  errorState?: string[] | null
  telemTxType?: number | null
  batteryCharging?: boolean | null
  batteryInstalled?: boolean | null
  batteryLow?: boolean | null
  batteryRemaining?: number | null
  uploadStatus?: UploadStatus | null
  isConnected?: boolean | null
  alarms?: string[] | null
}

export const getDeviceStateDataValueFromState = <T = StateItem['value']>(
  state: StdState,
  key: string
): T | null => {
  if (isValidObject(state)) {
    const deviceStateData = state.device_state_data
    if (isValidObject(deviceStateData)) {
      const deviceStateItem = deviceStateData[key]
      if (isValidObject(deviceStateItem)) {
        if (deviceStateItem.type === 'map' || deviceStateItem.type === 'upload_status') {
          // If the type is 'map', we return the value as an object
          // 'upload_status' is also kind of a map handled by flattenStateItemObject
          return flattenStateItemObject(deviceStateItem) as T
        }
        return deviceStateItem.value as T
      }
    }
  }

  return null
}

export const getCloudStateDataValueFromState = <T = StateItem['value']>(
  state: StdState,
  key: string
): T | null => {
  if (isValidObject(state)) {
    const cloudStateData = state.cloud_state_data
    if (isValidObject(cloudStateData)) {
      const cloudStateItem = cloudStateData[key]
      if (isValidObject(cloudStateItem)) return cloudStateItem.value as T
    }
  }

  return null
}

export const getDeviceDetailsFromState = (state: StdState): DeviceDetails => {
  const detail: DeviceDetails = {}

  detail.timestamp = state.timestamp
  detail.model = state.model
  detail.serial = state.serial

  if (state.cloud_state_data) {
    detail.name = getCloudStateDataValueFromState(state, 'name')
    detail.locationName = getCloudStateDataValueFromState(state, 'location_name')
    detail.indoor = getCloudStateDataValueFromState(state, 'is_indoor')
    detail.isPublic = getCloudStateDataValueFromState(state, 'is_public')
    detail.spsEoL = getCloudStateDataValueFromState(state, 'sps_eol')
    detail.zeroState = getCloudStateDataValueFromState(state, 'zero_state')
    detail.zeroAppliedAt = getCloudStateDataValueFromState(state, 'zero_applied_at')
  }

  if (state.device_state_data) {
    detail.config_timestamp = getDeviceStateDataValueFromState(state, 'config_timestamp')
    detail.firmware = getDeviceStateDataValueFromState(state, 'firmware_version')
    detail.mqttHost = getDeviceStateDataValueFromState(state, 'mqtt_host')
    detail.rssi = getDeviceStateDataValueFromState(state, 'ap_rssi')
    detail.connectionType = getDeviceStateDataValueFromState(state, 'connection_type')
    detail.calibrationDate = getDeviceStateDataValueFromState(state, 'calibration_date')
    detail.flowCalibrationFactor = getDeviceStateDataValueFromState(
      state,
      'flow_calibration_factor'
    )
    detail.userCal = getDeviceStateDataValueFromState(state, 'user_cal')
    detail.errorState = getDeviceStateDataValueFromState(state, 'error_state')
    detail.telemTxType = getDeviceStateDataValueFromState(state, 'telem_tx_type')
    detail.batteryCharging = getDeviceStateDataValueFromState(state, 'battery_charging')
    detail.batteryInstalled = getDeviceStateDataValueFromState(state, 'battery_installed')
    detail.batteryLow = getDeviceStateDataValueFromState(state, 'battery_low')
    detail.batteryRemaining = getDeviceStateDataValueFromState(state, 'battery_remaining')
    detail.uploadStatus = getDeviceStateDataValueFromState(state, 'upload_status')
    detail.isConnected = getDeviceStateDataValueFromState(state, 'connected')
    detail.alarms = getDeviceStateDataValueFromState(state, 'alarms')
  }

  return detail
}

export const getDeviceDetailsFromDevice = (device: Device): DeviceDetails => {
  const detail: DeviceDetails = {}

  detail.timestamp = device.timestamp
  detail.model = device.model
  detail.serial = device.serial

  if (device.cloud_state_data) {
    detail.name = device.cloud_state_data.name
    detail.locationName = device.cloud_state_data.location_name
    detail.indoor = device.cloud_state_data.isIndoor
    detail.isPublic = device.cloud_state_data.isPublic
    detail.zeroAppliedAt = device.cloud_state_data.zero_applied_at ?? null
    detail.zeroState = device.cloud_state_data.zero_state ?? null
  }

  if (device.device_state_data) {
    detail.config_timestamp = device.device_state_data.config_timestamp
    detail.firmware = device.device_state_data.firmware_version
    detail.rssi = device.device_state_data.ap_rssi
    detail.connectionType = device.device_state_data.connection_type
    detail.calibrationDate = device.device_state_data.calibration_date
    detail.flowCalibrationFactor = device.device_state_data.flow_calibration_factor
    detail.userCal = device.device_state_data.user_cal
    detail.errorState = device.device_state_data.error_state
    detail.telemTxType = device.device_state_data.telem_tx_type
    detail.batteryCharging = device.device_state_data.battery_charging
    detail.batteryInstalled = device.device_state_data.battery_installed
    detail.batteryLow = device.device_state_data.battery_low
    detail.batteryRemaining = device.device_state_data.battery_remaining
    detail.uploadStatus = device.device_state_data.upload_status
    detail.isConnected = device.device_state_data.connected
    detail.alarms = device.device_state_data.alarms
  }

  return detail
}
